/* eslint-disable no-param-reassign */
import {
  AbodeAggregateMarketConditions,
  AloftDateJSON,
  AloftPrice,
  AloftPriceJSON,
  AloftTime,
  AloftTimeJSON,
  CraftsmanAdjustmentsCalcVersion,
  CraftsmanCostDatasetVersion,
  CraftsmanReplacementCostCalcVersion,
  DepreciationSettingsV2JSON,
  EscalationType,
  InspectionData,
  MainPropertyType,
  MarketAdjustments,
  OrderFeature,
  PairedCostSettingsJSON,
  PropertyData,
  RevisionData,
  SalesComparisonApproachType,
  Tkv2MarketConditionsAttachmentBody,
  defaultEcoLifeFields,
  expected,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import stringify from 'fast-json-stable-stringify';

import { OrderState, SyncResult, SyncState } from '../types.ts';
import { addComps } from './actions/addComps.ts';
import { clearOrder } from './actions/clearOrder.ts';
import { loadNewComps } from './actions/loadNewComps.ts';
import { LoadCompPayload, LoadPropertyPayload, loadProperties } from './actions/loadProperties.ts';
import { removeComps } from './actions/removeComp.ts';
import { setSettings } from './actions/setSettings.ts';
import { submitEditOrderDetailsForm } from './actions/submitEditOrderDetailsForm.ts';

export const initialOrderState: OrderState = {
  id: '',
  compSequence: [],
  rentalCompSequence: [],
  compSaveQueue: [],
  deletedComps: [],
  orderFeatures: [],
  orderFeaturesLoaded: false,
  propertyType: undefined,
  effectiveDate: undefined,
  subjectLoaded: false,
  compsLoaded: false,
  marketAdjustments: {
    version: 1,
    cachedForms: {
      dateOfSale: {
        selectedMethod: 'none',
        methodState: {
          customMonthlyRate: {
            rate: 0,
          },
          customMonthlyPrice: {
            type: 'typicalHomeValue',
            analysisPeriod: 12,
            trendsAsPercentages: false,
            trends: [],
          },
        },
      },
      listToSale: {
        selectedMethod: 'none',
        methodState: {},
      },
    },
  },
  marketAdjustmentsLoaded: false,
  marketConditionsLoaded: false,
  adjustmentSettings: {
    rounding: {},
    roundingDefault: AloftPrice.zero.toJSON(),
    threshold: {},
    thresholdDefault: AloftPrice.zero.toJSON(),
  },
  adjustmentSettingsLoaded: false,
  datasetVersion: '2024.1',
  adjustmentVersion: '1.0',
  calculatorVersion: '1.0',
  depreciationSettings: {},
  depreciationSettingsLoaded: false,
  costMultiplier: undefined,
  isArchived: false,
  isDeleted: false,
  isTraining: false,
  isInspectifyOrder: false,
  hasArvOption: false,
  hasAvmOption: false,
  hasRentalConfiguration: false,
  orderNumber: undefined,
  inspectorType: undefined,
  clientName: undefined,
  borrowerName: undefined,
  appraisalType: undefined,
  inspectionDate: undefined,
  intendedUsers: undefined,
  intendedUse: undefined,
  appraiserInstructions: undefined,
  licenseState: undefined,
  licenseNumber: undefined,
  licenseExpiration: undefined,
  licenseType: undefined,
  signedAt: undefined,
  inspectionData: undefined,
  previousDesktopOrderID: undefined,
  revisionData: undefined,
  previousRevisionsData: undefined,
  isRevision: false,
  marketAggregateData: undefined,
  appraisifyName: undefined,
  appraisifyAddress: undefined,
  compTypes: {},
  isMock: false,
  startedAt: undefined,
  appraisalReportDisclaimer: undefined,
  isEscalated: false,
  escalationType: undefined,
  salesComparisonApproach: 'depreciated_cost',
  includeCostApproach: true,
  createdAt: AloftTime.now().toJSON(),
};

type AddCompsReducerPayload = {
  comps: PropertyData[];
  sequenceTarget: 'compSequence' | 'rentalCompSequence';
};

type LoadPropertiesReducerPayload = {
  properties: LoadPropertyPayload[];
  compSequence: string[];
  rentalCompSequence: string[];
};

const bumpEditRevision = (state: OrderState, syncKey: keyof OrderState) => {
  const syncState = state[syncKey] as SyncState;
  syncState.editRevision = (syncState.editRevision ?? 0) + 1;
};

const orderSlice = createSlice({
  name: 'order',
  initialState: initialOrderState,
  reducers: {
    setOrder(state, action: PayloadAction<string>) {
      state.id = action.payload;
    },
    setCompSequence(state, action: PayloadAction<string[]>) {
      state.compSequence = action.payload;
      bumpEditRevision(state as OrderState, 'compSequenceSync');
    },
    setRentalCompSequence(state, action: PayloadAction<string[]>) {
      state.rentalCompSequence = action.payload;
      bumpEditRevision(state as OrderState, 'rentalCompSequenceSync');
    },
    compSequenceMutationComplete(state, action: PayloadAction<SyncResult>) {
      const { payload } = action;
      const { status } = payload;

      if (isDefined(state.compSequenceSync)) {
        expected(state.compSequenceSync).status = status;

        if (status === 'success') {
          expected(state.compSequenceSync).serverRevision = payload.serverRevision;
        }
      }
    },
    rentalCompSequenceMutationComplete(state, action: PayloadAction<SyncResult>) {
      const { payload } = action;
      const { status } = payload;

      if (isDefined(state.rentalCompSequenceSync)) {
        expected(state.rentalCompSequenceSync).status = status;

        if (status === 'success') {
          expected(state.rentalCompSequenceSync).serverRevision = payload.serverRevision;
        }
      }
    },
    loadOrderFeatures(state, action: PayloadAction<OrderFeature[]>) {
      state.orderFeatures = [...new Set(action.payload)];
      state.orderFeaturesSync = {
        editRevision: 0,
        serverRevision: 0,
        status: 'success',
      };
      state.orderFeaturesLoaded = true;
    },
    setOrderFeatures(state, action: PayloadAction<OrderFeature[]>) {
      state.orderFeatures = [...new Set(action.payload)];
      bumpEditRevision(state as OrderState, 'orderFeaturesSync');
    },
    orderFeaturesMutationComplete(state, action: PayloadAction<SyncResult>) {
      const { payload } = action;
      const { status } = payload;

      if (isDefined(state.orderFeaturesSync)) {
        expected(state.orderFeaturesSync).status = status;

        if (status === 'success') {
          expected(state.orderFeaturesSync).serverRevision = payload.serverRevision;
        }
      }
    },
    loadMarketAdjustments(state, action: PayloadAction<MarketAdjustments>) {
      state.marketAdjustments = action.payload;
      state.marketAdjustmentsSync = {
        editRevision: 0,
        serverRevision: 0,
        status: 'success',
      };
      state.marketAdjustmentsLoaded = true;
    },
    loadMarketConditions(state, action: PayloadAction<Tkv2MarketConditionsAttachmentBody>) {
      if (action.payload.cachedState.marketAggregateData) {
        state.marketAggregateData = action.payload.cachedState.marketAggregateData;
      }
      state.marketConditionsLoaded = true;
    },
    addMarketAggregateData(state, action: PayloadAction<AbodeAggregateMarketConditions>) {
      if (!state.marketAggregateData) {
        state.marketAggregateData = [];
      }
      state.marketAggregateData = [...state.marketAggregateData, action.payload];
    },
    setMarketAdjustments(state, action: PayloadAction<MarketAdjustments>) {
      state.marketAdjustments = { ...state.marketAdjustments, ...action.payload }; // don't overwrite commentary
      bumpEditRevision(state as OrderState, 'marketAdjustmentsSync');
    },
    setMarketAdjustmentsCommentary(state, action: PayloadAction<{ commentary: string }>) {
      state.marketAdjustments.commentary = action.payload.commentary;
      bumpEditRevision(state as OrderState, 'marketAdjustmentsSync');
    },
    marketAdjustmentsMutationComplete(state, action: PayloadAction<SyncResult>) {
      const { payload } = action;
      const { status } = payload;

      if (isDefined(state.marketAdjustmentsSync)) {
        expected(state.marketAdjustmentsSync).status = status;
        if (status === 'success') {
          expected(state.marketAdjustmentsSync).serverRevision = payload.serverRevision;
        }
      }
    },
    loadAdjustmentSettings(state, action: PayloadAction<PairedCostSettingsJSON>) {
      // this is for when the adjustment settings are first loaded from the server
      state.adjustmentSettings = action.payload;
      state.adjustmentSettingsSync = {
        editRevision: 0,
        serverRevision: 0,
        status: 'success',
      };
      state.adjustmentSettingsLoaded = true;
    },
    toggleAdjustmentSetting(state, action: PayloadAction<'rounding' | 'threshold'>) {
      switch (action.payload) {
        case 'rounding':
          state.adjustmentSettings.enableRounding = !state.adjustmentSettings.enableRounding;
          break;
        case 'threshold':
          state.adjustmentSettings.enableThreshold = !state.adjustmentSettings.enableThreshold;
          break;
        default:
          console.error(`Unknown adjustment setting: ${action.payload as string}`);
          return;
      }
      bumpEditRevision(state as OrderState, 'adjustmentSettingsSync');
    },
    adjustmentSettingsMutationComplete(state, action: PayloadAction<SyncResult>) {
      const { payload } = action;
      const { status } = payload;

      if (isDefined(state.adjustmentSettingsSync)) {
        expected(state.adjustmentSettingsSync).status = status;
        if (status === 'success') {
          expected(state.adjustmentSettingsSync).serverRevision = payload.serverRevision;
          expected(state.adjustmentSettingsSync).saveAsDefault = undefined;
        }
      }
    },
    loadOrderData(
      state,
      action: PayloadAction<{
        effectiveDate: AloftDateJSON;
        craftsmanAdjustmentVersion: CraftsmanAdjustmentsCalcVersion;
        craftsmanCalculatorVersion: CraftsmanReplacementCostCalcVersion;
        craftsmanDatasetVersion: CraftsmanCostDatasetVersion;
        propertyType: MainPropertyType;
        costMultiplier: number | undefined;
        isArchived: boolean;
        isDeleted: boolean;
        isTraining: boolean;
        isInspectifyOrder: boolean;
        hasArvOption: boolean;
        hasAvmOption: boolean;
        hasRentalConfiguration: boolean;
        orderNumber?: string;
        inspectorType?: string;
        clientName?: string;
        borrowerName?: string;
        appraisalType?: string;
        inspectionDate?: AloftDateJSON;
        intendedUsers?: string;
        intendedUse?: string;
        appraiserInstructions?: string;
        licenseState?: string;
        licenseNumber?: string;
        licenseExpiration?: AloftDateJSON;
        licenseType?: string;
        signedAt?: AloftTimeJSON;
        inspectionData?: InspectionData;
        previousDesktopOrderID?: string;
        revisionData?: RevisionData;
        previousRevisionsData?: RevisionData[];
        inspectionId?: string;
        revisionRequestId?: string;
        isRevision: boolean;
        appraisifyName?: string;
        appraisifyAddress?: string;
        isMock: boolean;
        appraisifyFee?: AloftPriceJSON;
        appraiserFee?: AloftPriceJSON;
        startedAt?: AloftTimeJSON;
        appraisalReportDisclaimer?: string;
        isEscalated: boolean;
        escalationType?: EscalationType;
        salesComparisonApproach: SalesComparisonApproachType;
        includeCostApproach: boolean;
        createdAt: AloftTimeJSON;
      }>,
    ) {
      state.effectiveDate = action.payload.effectiveDate;
      state.adjustmentVersion = action.payload.craftsmanAdjustmentVersion;
      state.calculatorVersion = action.payload.craftsmanCalculatorVersion;
      state.datasetVersion = action.payload.craftsmanDatasetVersion;
      state.propertyType = action.payload.propertyType;
      state.costMultiplier = action.payload.costMultiplier;
      state.isArchived = action.payload.isArchived;
      state.isDeleted = action.payload.isDeleted;
      state.isTraining = action.payload.isTraining;
      state.isInspectifyOrder = action.payload.isInspectifyOrder;
      state.hasArvOption = action.payload.hasArvOption;
      state.hasAvmOption = action.payload.hasAvmOption;
      state.hasRentalConfiguration = action.payload.hasRentalConfiguration;
      state.orderNumber = action.payload.orderNumber;
      state.clientName = action.payload.clientName;
      state.inspectorType = action.payload.inspectorType;
      state.borrowerName = action.payload.borrowerName;
      state.appraisalType = action.payload.appraisalType;
      state.inspectionDate = action.payload.inspectionDate;
      state.intendedUsers = action.payload.intendedUsers;
      state.intendedUse = action.payload.intendedUse;
      state.appraiserInstructions = action.payload.appraiserInstructions;
      state.licenseState = action.payload.licenseState;
      state.licenseNumber = action.payload.licenseNumber;
      state.licenseExpiration = action.payload.licenseExpiration;
      state.licenseType = action.payload.licenseType;
      state.signedAt = action.payload.signedAt;
      state.inspectionData = action.payload.inspectionData;
      state.previousDesktopOrderID = action.payload.previousDesktopOrderID;
      state.revisionData = action.payload.revisionData;
      state.previousRevisionsData = action.payload.previousRevisionsData;
      state.inspectionId = action.payload.inspectionId;
      state.isRevision = action.payload.isRevision;
      state.appraisifyName = action.payload.appraisifyName;
      state.appraisifyAddress = action.payload.appraisifyAddress;
      state.isMock = action.payload.isMock;
      state.appraisifyFee = action.payload.appraisifyFee;
      state.appraiserFee = action.payload.appraiserFee;
      state.startedAt = action.payload.startedAt;
      state.appraisalReportDisclaimer = action.payload.appraisalReportDisclaimer;
      state.isEscalated = action.payload.isEscalated;
      state.escalationType = action.payload.escalationType;
      state.salesComparisonApproach = action.payload.salesComparisonApproach;
      state.includeCostApproach = action.payload.includeCostApproach;
      state.createdAt = action.payload.createdAt;

      if (isUndefined(state.detailsSync)) {
        state.detailsSync = {
          editRevision: 0,
          serverRevision: 0,
          status: 'success',
        };
      }
    },
    loadDepreciationSettings(state, action: PayloadAction<DepreciationSettingsV2JSON>) {
      state.depreciationSettings = action.payload;
      state.depreciationSettingsSync = {
        editRevision: 0,
        serverRevision: 0,
        status: 'success',
      };
      state.depreciationSettingsLoaded = true;
    },
    startOrder(state) {
      state.startedAt = AloftTime.now().toJSON();
      bumpEditRevision(state as OrderState, 'detailsSync');
    },
    toggleDepreciationSettings(
      state,
      action: PayloadAction<'depreciateSeparately' | 'functional' | 'external'>,
    ) {
      switch (action.payload) {
        case 'depreciateSeparately':
          state.depreciationSettings.depreciateSeparately =
            !state.depreciationSettings.depreciateSeparately;
          break;
        case 'functional':
          state.depreciationSettings.enableFunctionalDepreciation =
            !state.depreciationSettings.enableFunctionalDepreciation;
          break;
        case 'external':
          state.depreciationSettings.enableExternalDepreciation =
            !state.depreciationSettings.enableExternalDepreciation;
          break;
        default:
          console.error(`Unknown depreciation setting: ${action.payload as string}`);
          return;
      }
      bumpEditRevision(state as OrderState, 'depreciationSettingsSync');
    },
    depreciationSettingsMutationComplete(state, action: PayloadAction<SyncResult>) {
      const { payload } = action;
      const { status } = payload;

      if (isDefined(state.depreciationSettingsSync)) {
        expected(state.depreciationSettingsSync).status = status;
        if (status === 'success') {
          expected(state.depreciationSettingsSync).serverRevision = payload.serverRevision;
          expected(state.depreciationSettingsSync).saveAsDefault = undefined;
        }
      }
    },
    addCompsPosted(state) {
      state.compSaveQueue = state.compSaveQueue.slice(1);
    },
    submitCostMultiplierForm(state, action: PayloadAction<{ costMultiplier: number | undefined }>) {
      state.costMultiplier = action.payload.costMultiplier;
      bumpEditRevision(state as OrderState, 'detailsSync');
    },
    orderDetailsMutationComplete(state, action: PayloadAction<SyncResult>) {
      const { payload } = action;
      const { status } = payload;

      if (isDefined(state.detailsSync)) {
        expected(state.detailsSync).status = status;
        if (status === 'success') {
          expected(state.detailsSync).serverRevision = payload.serverRevision;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearOrder, () => initialOrderState);
    builder.addCase(addComps, (state, action) => {
      const payload = action.payload as unknown as AddCompsReducerPayload;
      if (payload.comps.length > 0) {
        state.compSaveQueue.push({
          comps: payload.comps,
          sequenceTarget: payload.sequenceTarget,
        });
      }
    });
    builder.addCase(loadNewComps, (state, action) => {
      state.compSequence = action.payload.compSequence.filter(
        (id) => !state.deletedComps.includes(id),
      );
      state.rentalCompSequence = action.payload.rentalCompSequence.filter(
        (id) => !state.deletedComps.includes(id),
      );
      state.deletedComps = [];
      action.payload.comps.forEach((comp) => {
        (state.compTypes || {})[comp.id] = comp.compType;
      });
    });
    builder.addCase(loadProperties, (state, action) => {
      const payload = action.payload as LoadPropertiesReducerPayload;
      // this is for when the properties and comp sequence are first
      // loaded from the server
      if (payload.properties.filter(({ id }) => id === 'subject').length >= 0) {
        state.subjectLoaded = true;
      }
      state.compSequence = payload.compSequence;
      state.rentalCompSequence = payload.rentalCompSequence;
      state.compTypes = Object.fromEntries(
        [...payload.compSequence, ...payload.rentalCompSequence].map((compId) => [
          compId,
          (payload.properties.find(({ id }) => id === compId) as LoadCompPayload)?.compType,
        ]),
      );
      state.compSequenceSync = {
        editRevision: 0,
        serverRevision: 0,
        status: 'success',
      };
      state.rentalCompSequenceSync = {
        editRevision: 0,
        serverRevision: 0,
        status: 'success',
      };
      state.compsLoaded = true;
    });
    builder.addCase(setSettings, (state, action) => {
      let bumpAdjustmentSettings = false;
      let bumpDepreciationSettings = false;
      let bumpCostMultiplierSettings = false;

      if (action.payload.saveAsDefault) {
        if (state.adjustmentSettingsSync) {
          state.adjustmentSettingsSync.saveAsDefault = true;
        }
        if (state.depreciationSettingsSync) {
          state.depreciationSettingsSync.saveAsDefault = true;
        }
      }

      if (action.payload.roundingDefault !== state.adjustmentSettings.roundingDefault) {
        state.adjustmentSettings.roundingDefault =
          action.payload.roundingDefault ?? AloftPrice.zero.toJSON();
        bumpAdjustmentSettings = true;
      }
      if (stringify(action.payload.rounding) !== stringify(state.adjustmentSettings.rounding)) {
        state.adjustmentSettings.rounding = action.payload.rounding ?? {};
        bumpAdjustmentSettings = true;
      }

      if (action.payload.thresholdDefault !== state.adjustmentSettings.thresholdDefault) {
        state.adjustmentSettings.thresholdDefault =
          action.payload.thresholdDefault ?? AloftPrice.zero.toJSON();
        bumpAdjustmentSettings = true;
      }
      if (stringify(action.payload.threshold) !== stringify(state.adjustmentSettings.threshold)) {
        state.adjustmentSettings.threshold = action.payload.threshold ?? {};
        bumpAdjustmentSettings = true;
      }

      if (action.payload.depreciateSeparately !== state.depreciationSettings.depreciateSeparately) {
        state.depreciationSettings.depreciateSeparately = action.payload.depreciateSeparately;
        bumpDepreciationSettings = true;
      }

      if (
        action.payload.enableFunctionalDepreciation !==
        state.depreciationSettings.enableFunctionalDepreciation
      ) {
        state.depreciationSettings.enableFunctionalDepreciation =
          action.payload.enableFunctionalDepreciation;
        bumpDepreciationSettings = true;
      }

      if (
        action.payload.enableExternalDepreciation !==
        state.depreciationSettings.enableExternalDepreciation
      ) {
        state.depreciationSettings.enableExternalDepreciation =
          action.payload.enableExternalDepreciation;
        bumpDepreciationSettings = true;
      }

      defaultEcoLifeFields.forEach((field) => {
        if (action.payload[field] !== state.depreciationSettings[field]) {
          state.depreciationSettings[field] = action.payload[field];
          bumpDepreciationSettings = true;
        }
      });

      if (action.payload.costMultiplier !== state.costMultiplier) {
        state.costMultiplier = action.payload.costMultiplier;
        bumpCostMultiplierSettings = true;
      }

      if (bumpAdjustmentSettings) {
        bumpEditRevision(state as OrderState, 'adjustmentSettingsSync');
      }
      if (bumpDepreciationSettings) {
        bumpEditRevision(state as OrderState, 'depreciationSettingsSync');
      }
      if (bumpCostMultiplierSettings) {
        bumpEditRevision(state as OrderState, 'detailsSync');
      }
    });
    builder.addCase(submitEditOrderDetailsForm, (state, action) => {
      const { effectiveDate, inspectionDate, intendedUsers, intendedUse } = action.payload;

      Object.assign(state, {
        effectiveDate,
        inspectionDate,
        intendedUsers,
        intendedUse,
      });

      bumpEditRevision(state as OrderState, 'detailsSync');
    });
    builder.addCase(removeComps, (state, action) => {
      const target = action.payload.sequenceTarget ?? 'compSequence';
      if (target === 'rentalCompSequence') {
        state.rentalCompSequence = state.rentalCompSequence.filter(
          (id) => !action.payload.ids.includes(id),
        );
        bumpEditRevision(state as OrderState, 'rentalCompSequenceSync');
      } else {
        state.compSequence = state.compSequence.filter((id) => !action.payload.ids.includes(id));
        bumpEditRevision(state as OrderState, 'compSequenceSync');
      }
      state.deletedComps = state.deletedComps.concat(action.payload.ids);
    });
  },
});

export const {
  setOrder,
  setCompSequence,
  setRentalCompSequence,
  setOrderFeatures,
  loadOrderFeatures,
  compSequenceMutationComplete,
  rentalCompSequenceMutationComplete,
  orderFeaturesMutationComplete,
  loadMarketAdjustments,
  loadMarketConditions,
  setMarketAdjustments,
  setMarketAdjustmentsCommentary,
  marketAdjustmentsMutationComplete,
  addMarketAggregateData,
  loadAdjustmentSettings,
  adjustmentSettingsMutationComplete,
  toggleAdjustmentSetting,
  loadDepreciationSettings,
  depreciationSettingsMutationComplete,
  toggleDepreciationSettings,
  addCompsPosted,
  loadOrderData,
  submitCostMultiplierForm,
  orderDetailsMutationComplete,
  startOrder,
} = orderSlice.actions;

export const orderReducer = orderSlice.reducer;
